import { projectApi } from '@shared/api-client';
import { useQuery } from '@tanstack/react-query';
import { Database } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { useSession } from '@/hooks/use-session';
import { ImportsSection } from '@/pages/settings/components/imports-section';
import { IntegrationsSection } from '@/pages/settings/components/integrations-section';
import { RegistrationTrackingSection } from '@/pages/settings/components/registration-tracking-section';
import { ApiKeysSection } from '@/pages/settings/tabs/projects';

export const ProjectDataSourcesPage = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useSession();
  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectApi.getProject(projectId!),
    enabled: Boolean(projectId),
  });

  const role = user?.currentOrganization.role;
  const canManage = role === 'owner' || role === 'admin';
  const canCreateKeys = canManage || role === 'member';

  if (projectQuery.isLoading) {
    return (
      <div className="mx-auto max-w-4xl space-y-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-48 w-full rounded-card" />
        <Skeleton className="h-48 w-full rounded-card" />
      </div>
    );
  }

  if (!projectQuery.data) {
    return (
      <Card className="mx-auto max-w-2xl shadow-none">
        <EmptyState
          icon={Database}
          title="Project not found"
          description="It may have been deleted or belong to another organization."
          action={
            <Button asChild variant="secondary">
              <Link to="/">Back to projects</Link>
            </Button>
          }
        />
      </Card>
    );
  }

  const project = projectQuery.data;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-ink">
          Data sources
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-muted">
          Control how {project.name} sends product activity, revenue, and
          historical analytics into GainSignal.
        </p>
      </div>

      {canManage && (
        <Button asChild variant="secondary">
          <Link to={`/projects/${project.id}/setup`}>Open guided setup</Link>
        </Button>
      )}
      <div className="space-y-4">
        <Card className="overflow-hidden shadow-none">
          <ApiKeysSection
            project={project}
            canManage={canManage}
            canCreate={canCreateKeys}
          />
        </Card>

        <Card className="overflow-hidden shadow-none">
          <RegistrationTrackingSection project={project} />
        </Card>

        <Card className="overflow-hidden shadow-none">
          <IntegrationsSection project={project} canManage={canManage} />
        </Card>

        <Card className="overflow-hidden shadow-none">
          <ImportsSection project={project} canManage={canManage} />
        </Card>
      </div>
    </div>
  );
};
